import {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Opt} from 'ts-opt';

import {AssociationType} from 'api/gen/AssociationType';
import {CriteriaDefinition} from 'api/gen/CriteriaDefinition';
import {Sex} from 'api/gen/Sex';
import {matingAction, matingSelector} from 'app/mating/model';
import {SessionState} from 'app/mating/model/state';
import {simpleTableSelector} from 'utils/tables';

interface UseAnimalTabProps {
    /** Association type that drives the tab queries. */
    associationType: AssociationType;
    /** Sex-specific tab mode. */
    sex: Sex;
}

interface UseAnimalTabResult {
    /** Current animal lists loaded for the tab. */
    animalLists: ReturnType<typeof matingSelector.animalLists>;
    /** Current session state for the tab. */
    animalsSessionState: SessionState;
    /** Criteria accordion definitions filtered for the current sex. */
    criteriaAccordions: ReturnType<typeof matingSelector.criteriaAccordions>;
    /** Form criteria definitions for the current sex. */
    criteriaDefinitions: Opt<Array<CriteriaDefinition>>;
    /** Saved criteria lists available to the user. */
    criteriaLists: ReturnType<typeof matingSelector.criteriaLists>;
    /** Whether the current animals page is empty. */
    isAnimalsPageEmpty: boolean;
    /** Loading flags relevant to the tab. */
    loading: ReturnType<typeof matingSelector.loading>;
    /** Saved table settings names visible in the current tab. */
    tableNameLists: Array<string>;
    /** Triggers the tab filter action. */
    filterAnimals(): void;
    /** Opens or closes the add-from-DB modal. */
    toggleAddAnimalsFromDBVisibility(): void;
    /** Opens or closes the add-animal modal. */
    toggleAddAnimalsVisibility(): void;
    /** Opens or closes the saved-animal-list modal. */
    toggleAnimalListsVisibility(): void;
    /** Opens or closes the criteria-list-details modal. */
    toggleCriteriaListDetailsVisibility(): void;
    /** Opens or closes the saved criteria list modal. */
    toggleCriteriaListsVisibility(): void;
    /** Opens or closes the overview modal for saved lists. */
    toggleListsOverviewVisibility(): void;
}

const getEmptyStateSelector = (
    sex: Sex,
): ((state: Parameters<typeof matingSelector.isCowsSessionEmpty>[0]) => boolean) =>
    sex === 'FEMALE' ? matingSelector.isCowsSessionEmpty : matingSelector.isBullsSessionEmpty;

const getSessionStateSelector = (
    sex: Sex,
): ((state: Parameters<typeof matingSelector.cowsSessionState>[0]) => SessionState) =>
    sex === 'FEMALE' ? matingSelector.cowsSessionState : matingSelector.bullsSessionState;

const getCriteriaDefinitionsSelector = (
    sex: Sex,
): ((state: Parameters<typeof matingSelector.cowCriteriaDefinitions>[0]) => Opt<Array<CriteriaDefinition>>) =>
    sex === 'FEMALE' ? matingSelector.cowCriteriaDefinitions : matingSelector.bullCriteriaDefinitions;

const getTableCode = (sex: Sex): 'COW' | 'BULL' => (sex === 'FEMALE' ? 'COW' : 'BULL');

/**
 * Builds the data and handlers for a mating animal tab.
 *
 * @param props Association type and sex for the active tab.
 * @returns Tab data, loading state, and action handlers.
 */
// eslint-disable-next-line max-lines-per-function
export const useAnimalTab = ({associationType, sex}: UseAnimalTabProps): UseAnimalTabResult => {
    const dispatch = useDispatch();
    const loading = useSelector(matingSelector.loading);
    const animalLists = useSelector(matingSelector.animalLists);
    const criteriaLists = useSelector(matingSelector.criteriaLists);
    const criteriaAccordions = useSelector(matingSelector.criteriaAccordions);
    const tableNameLists = useSelector(simpleTableSelector.userSettings(getTableCode(sex)))?.map(x => x.name) || [];
    const isAnimalsPageEmpty = useSelector(getEmptyStateSelector(sex));
    const animalsSessionState = useSelector(getSessionStateSelector(sex));
    const criteriaDefinitions = useSelector(getCriteriaDefinitionsSelector(sex));
    const filteredCriteriaAccordions = useMemo(() =>
        criteriaAccordions.filterIn(x => x.bookmark === sex),
    [criteriaAccordions, sex]);

    useEffect(() => {
        dispatch(matingAction.getAnimalLists(sex, associationType));
        dispatch(matingAction.getCriteriaLists(sex, associationType));
    }, [associationType, dispatch, sex]);

    return {
        animalLists,
        animalsSessionState,
        criteriaAccordions: filteredCriteriaAccordions,
        criteriaDefinitions,
        criteriaLists,
        filterAnimals: useCallback((): void => {
            dispatch(matingAction.filterAnimals(sex, associationType));
        }, [associationType, dispatch, sex]),
        isAnimalsPageEmpty,
        loading,
        tableNameLists,
        toggleAddAnimalsFromDBVisibility: useCallback((): void => {
            dispatch(matingAction.toggleAddAnimalsFromDBVisibility(sex));
        }, [dispatch, sex]),
        toggleAddAnimalsVisibility: useCallback((): void => {
            dispatch(matingAction.toggleAddAnimalsVisibility(sex));
        }, [dispatch, sex]),
        toggleAnimalListsVisibility: useCallback((): void => {
            dispatch(matingAction.toggleAnimalListsVisibility(sex));
        }, [dispatch, sex]),
        toggleCriteriaListDetailsVisibility: useCallback((): void => {
            dispatch(matingAction.toggleCriteriaListDetailsVisibility(sex));
        }, [dispatch, sex]),
        toggleCriteriaListsVisibility: useCallback((): void => {
            dispatch(matingAction.toggleCriteriaListsVisibility(sex));
        }, [dispatch, sex]),
        toggleListsOverviewVisibility: useCallback((): void => {
            dispatch(matingAction.toggleListsOverviewVisibility(sex));
        }, [dispatch, sex]),
    };
};
